// Review parsed values (`#candidates/review/<parse_id>`, DS DC-83 / candidate C-6): "Parsing review is two panes: the
// original resume beside the seven proposed values (*Proposed* → *Confirmed*); Save waits for every value." The pane
// shows the resume's text (the CSP forbids embedding the file itself); each of his seven values is confirmed with a
// tick or edited, and Save stays blocked with "Human confirmation before parsed values are authoritative." until all
// seven are. Save then runs the potential-matches gate like manual entry. While the worker parses, the page polls;
// a failed parse is the clear error for the file and nothing else ("without creating a partial profile"). Only the
// uploader, the owning Recruiter and the TA roles may open it; anyone else gets the not-found page. The card carries no
// requirement sentence as its subtitle (UAT F-32, Q-4's default): the seven values and their states say it.
// Hosted by MissionShell: views/b3/register.ts registers it through S12's registerView, so it draws no McRoot of its own.

import { useEffect, useMemo, useState } from "react";
import type { CandidateParseOpenOutput, EmploymentRow, ParsedEdits } from "@/api/tools";
import { candidateParseOpen } from "@/api/tools";
import { isClientRefusal } from "@/api/useTool";
import { go } from "@/router";
import { useSession } from "@/session/useSession";
import { useViewState } from "@/shell/viewState";
import { NotFound } from "@/views/NotFound";
import {
  ACTION_LABELS, Banner, Button, Card, CheckTile, COMMON_LABELS, DocPane, ErrorStrip, Field, FieldState, Flex, G1_LABELS, IdPill,
  Input, Lines, PageHeader, PersonPick, Recorded, RULE_LABELS, Select, Skeleton, Stack, TextArea, TextLink,
} from "@/mc";
import { BRD } from "./brd";
import { mayResolveMatches, fileError, fieldError, useOwnerChoices } from "./AddCandidateDialog";
import { useSourceChoices } from "./options";
import { useSettledRead } from "@/views/shared/settledRead";
import { CANDIDATE_LABELS as L, CANDIDATE_RULES } from "./labels";
import { PotentialMatches, type Entry } from "./PotentialMatches";
import { gateBlocked, useSaveGate } from "./save";

type TextField = "name" | "email" | "phone" | "location" | "current_employer" | "linkedin_url";
type ParsedField = TextField | "employment_history";

/** His seven values, in his order (BRD 10.3). */
const FIELDS: readonly { key: ParsedField; label: string }[] = [
  { key: "name", label: L.name },
  { key: "email", label: L.email },
  { key: "phone", label: L.phone },
  { key: "location", label: L.location },
  { key: "current_employer", label: BRD.currentEmployer },
  { key: "employment_history", label: BRD.employmentHistory },
  { key: "linkedin_url", label: L.linkedinUrl },
];

const asText = (value: unknown): string => (typeof value === "string" ? value : "");
const asRows = (value: unknown): EmploymentRow[] => (Array.isArray(value) ? (value as EmploymentRow[]) : []);

export function ReviewParsedPage({ parseId }: { parseId: string }) {
  return <ReviewBody parseId={parseId} />;
}

function ReviewBody({ parseId }: { parseId: string }) {
  const { register } = useViewState();
  const parse = useSettledRead(["candidate-parse", parseId], () => candidateParseOpen({ parse_id: parseId }), {
    refetchInterval: (query) => (query.state.data?.status === "pending" ? 3_000 : false),
  });
  const status = parse.data?.status;
  useEffect(() => register({ view: "candidates", ids: { parse: parseId }, summary: { status: status ?? "loading" } }), [register, parseId, status]);

  if (parse.error && isClientRefusal(parse.error)) return <NotFound route={`candidates/review/${parseId}`} />;
  const pending = parse.isLoading || status === "pending";
  return (
    <Stack gap={16}>
      <PageHeader title={L.reviewParsedValues} loading={parse.isLoading} />
      {parse.error ? (
        <ErrorStrip message={RULE_LABELS.failedSyncRetryable} problem={{ status: parse.error.status, refusal_code: parse.error.code, request_id: parse.error.requestId }} onRetry={() => void parse.refetch()}  />
      ) : null}
      {status === "failed" ? (
        <DocPane error={fileError(parse.data?.failure_code)} />
      ) : status === "confirmed" && parse.data ? (
        <Recorded right={parse.data.candidate_no ? <IdPill value={parse.data.candidate_no} /> : undefined}>
          {parse.data.candidate_id ? <TextLink href={`#candidates/${parse.data.candidate_id}`}>{COMMON_LABELS.candidateId}</TextLink> : COMMON_LABELS.candidateId}
        </Recorded>
      ) : pending || !parse.data ? (
        <DocPane loading fields={<Skeleton lines={14} height={18} />} />
      ) : (
        <Review parse={parse.data} />
      )}
    </Stack>
  );
}

function Review({ parse }: { parse: CandidateParseOpenOutput }) {
  const { me } = useSession();
  const owner = useOwnerChoices();
  const sourceChoices = useSourceChoices();
  const mayResolve = mayResolveMatches(me?.roles ?? []);
  const proposed = parse.proposed ?? {};
  const initial = useMemo(() => Object.fromEntries(FIELDS.map(({ key }) => [key, asText(proposed[key]?.value)])) as Record<TextField, string>, [proposed]);
  const [values, setValues] = useState<Record<TextField, string>>(initial);
  const [edited, setEdited] = useState<ReadonlySet<ParsedField>>(new Set());
  const [confirmed, setConfirmed] = useState<ReadonlySet<ParsedField>>(new Set());
  const [source, setSource] = useState("");
  const [ownerId, setOwnerId] = useState(parse.owner_id ?? "");
  const [noEmail, setNoEmail] = useState(false);
  const [noEmailReason, setNoEmailReason] = useState("");
  const [page, setPage] = useState(1);
  const pages = (parse.text ?? "").split("\f");

  const done = (key: ParsedField) => edited.has(key) || confirmed.has(key);
  const allDone = FIELDS.every(({ key }) => done(key));
  const edit = (key: TextField, value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setEdited((prev) => new Set(prev).add(key));
  };
  const tick = (key: ParsedField, on: boolean) => setConfirmed((prev) => {
    const next = new Set(prev);
    if (on) next.add(key);
    else next.delete(key);
    return next;
  });

  const entry: Entry = { name: values.name, email: values.email, secondaryEmail: "", phone: values.phone, linkedinUrl: values.linkedin_url };
  const ownerOf = ownerId || (owner.recruiter ? owner.self : "");
  const gate = useSaveGate({
    entry,
    requisitionId: parse.requisition_id ?? null,
    mayResolve,
    build: () => {
      const edits: ParsedEdits = {};
      for (const key of edited) if (key !== "employment_history") edits[key] = values[key].trim() || null;
      return {
        parse_id: parse.parse_id,
        confirm: FIELDS.map(({ key }) => key).filter((key) => confirmed.has(key) && !edited.has(key)),
        edits: Object.keys(edits).length > 0 ? edits : null,
        source_channel: source,
        owner_id: ownerOf || null,
        no_email_reason: !values.email.trim() && noEmail ? noEmailReason.trim() : null,
        requisition_id: parse.requisition_id ?? null,
      };
    },
    onSaved: (out) => go(`#candidates/${out.candidate_id}`),
  });

  const sourced = source === "sourced";
  const emailMissing = !values.email.trim();
  const required = Boolean(source && ownerOf && (!emailMissing || (noEmail && sourced && noEmailReason.trim().length >= 3)));
  const blocked = !allDone
    ? { reason: CANDIDATE_RULES.humanConfirmation }
    : !required ? { reason: CANDIDATE_RULES.manualEntry }
    : gateBlocked(gate, mayResolve) ? { reason: CANDIDATE_RULES.potentialMatch } : undefined;

  const fields = (
    <Card
      title={L.reviewParsedValues}
      refused={gate.refused && gate.hits === null ? { reason: gate.refused } : undefined}
    >
      <Stack gap={12}>
        {gate.failure ? (
          <ErrorStrip message={RULE_LABELS.failedSyncRetryable} problem={{ status: gate.failure.status, refusal_code: gate.failure.code, request_id: gate.failure.requestId }} onRetry={() => { void gate.save(); }} />
        ) : null}
        {FIELDS.map(({ key, label }) => (
          <Field key={key} label={label} labelSlot={<FieldState kind="parsing" done={done(key)} />} error={key === "employment_history" ? undefined : fieldError(gate.fieldErrors, `edits.${key}`)}>
            {key === "employment_history" ? (
              <Stack gap={4}>
                {asRows(proposed.employment_history?.value).map((row, index) => (
                  <span key={index}>{[row.employer, row.title, [row.start, row.end].filter(Boolean).join(" – ")].filter(Boolean).join(" · ")}</span>
                ))}
                <CheckTile plain checked={done(key)} onChange={(on) => tick(key, on)} >{G1_LABELS.confirmed}</CheckTile>
              </Stack>
            ) : (
              <Stack gap={6}>
                <Input value={values[key]} onChange={(event) => edit(key, event.target.value)} autoComplete="off" hs={`cand.review.${key.replace(/_/g, "-")}.value`} />
                <CheckTile plain checked={done(key)} onChange={(on) => tick(key, on)} blocked={edited.has(key) ? { reason: CANDIDATE_RULES.humanConfirmation } : undefined} hs={`cand.review.${key.replace(/_/g, "-")}`}>
                  {G1_LABELS.confirmed}
                </CheckTile>
              </Stack>
            )}
          </Field>
        ))}
        <Field label={L.source} required error={fieldError(gate.fieldErrors, "source_channel")}>
          <Select options={sourceChoices} value={source} onChange={setSource}  />
        </Field>
        <Field label={L.owner} required={!owner.recruiter} error={fieldError(gate.fieldErrors, "owner_id")}>
          <PersonPick
            options={owner.choices.map((choice) => ({ value: choice.value, name: choice.label }))}
            value={ownerOf || undefined}
            onChange={setOwnerId}
            loading={owner.loading}
            
          />
        </Field>
        {emailMissing && sourced ? (
          <>
            <CheckTile checked={noEmail} onChange={setNoEmail} sub={CANDIDATE_RULES.noEmail} >{L.noEmailException}</CheckTile>
            {noEmail ? (
              <Field label={G1_LABELS.reason} required hintTone="flag" hint={CANDIDATE_RULES.noEmail}>
                <TextArea rows={2} value={noEmailReason} onChange={(event) => setNoEmailReason(event.target.value)}  />
              </Field>
            ) : null}
          </>
        ) : null}
        {gate.hits !== null && gate.hits.length > 0 ? (
          <PotentialMatches
            hits={gate.hits}
            entry={entry}
            mayResolve={mayResolve}
            requisitionId={parse.requisition_id}
            onUseExisting={(hit) => { void gate.useExisting(hit); }}
            onOpenProfile={(id) => go(`#candidates/${id}`)}
            busy={gate.busy}
            distinct={gate.distinct}
            onDistinct={gate.setDistinct}
            reason={gate.reason}
            onReason={gate.setReason}
            refused={gate.refused}
          />
        ) : null}
        <Flex justify="end">
          <Button tone="primary" onClick={() => { void gate.save(); }} blocked={blocked} busy={gate.busy} >{ACTION_LABELS.save}</Button>
        </Flex>
      </Stack>
    </Card>
  );

  return (
    <>
      {parse.status === "parsed" ? null : <Banner tone="info">{CANDIDATE_RULES.proposedForReview}</Banner>}
      <DocPane pages={pages.length} page={page} onPage={setPage} fields={fields} >
        {(n: number) => <Lines plain sm items={(pages[n - 1] ?? "").split("\n")} />}
      </DocPane>
    </>
  );
}
